/**
 * Isometric projection helpers — pure functions, no PixiJS dependency.
 *
 * Coordinate system (CLIENT_DESIGN §4.1, locked):
 *   - Tile grid is 2D integer coordinates, `(x, y) ∈ ℤ²`.
 *   - Tiles render as 2:1 diamonds: width 128 px × height 64 px at zoom 1.
 *   - Screen X = (tile.x - tile.y) * halfW
 *     Screen Y = (tile.x + tile.y) * halfH
 *   - The camera pans and zooms the entire world container. World-to-screen
 *     is a separate step (camera-world transform) handled by the PixiJS
 *     layer via container transforms.
 */

export const TILE_WIDTH_PX = 128;
export const TILE_HEIGHT_PX = 64;

/** World coords → iso-projected local coords (pre-camera). */
export function tileToIso(
  x: number,
  y: number,
): { x: number; y: number } {
  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;
  return {
    x: (x - y) * halfW,
    y: (x + y) * halfH,
  };
}

/** Inverse of `tileToIso`. Returned tile coords are real-valued; caller
 *  rounds/floors to integer tiles as needed (e.g. for hit-testing). */
export function isoToTile(
  px: number,
  py: number,
): { x: number; y: number } {
  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;
  // Invert the 2-by-2 linear map:
  //   | halfW  -halfW | | x |   | px |
  //   | halfH   halfH | | y | = | py |
  // Determinant = halfW*halfH + halfW*halfH = 2 * halfW * halfH.
  const x = (px / halfW + py / halfH) / 2;
  const y = (py / halfH - px / halfW) / 2;
  return { x, y };
}

/** Camera state: pan (in world pixels, post-projection) + zoom. */
export interface CameraState {
  /** Pan offset in pre-projection world coordinates (iso space). */
  panX: number;
  panY: number;
  /** Uniform scale. Clamped to [minZoom, maxZoom] by the camera controller. */
  zoom: number;
}

export const DEFAULT_CAMERA: CameraState = {
  panX: 0,
  panY: 0,
  zoom: 1,
};

/** Compose iso projection + camera transform into final screen px. */
export function worldToScreen(
  tileX: number,
  tileY: number,
  cam: CameraState,
  viewportW: number,
  viewportH: number,
): { x: number; y: number } {
  const iso = tileToIso(tileX, tileY);
  // Centre the world origin in the viewport, then apply pan + zoom.
  return {
    x: (iso.x - cam.panX) * cam.zoom + viewportW / 2,
    y: (iso.y - cam.panY) * cam.zoom + viewportH / 2,
  };
}

/** Inverse of `worldToScreen` — used by click-to-inspect hit testing. */
export function screenToWorld(
  screenX: number,
  screenY: number,
  cam: CameraState,
  viewportW: number,
  viewportH: number,
): { x: number; y: number } {
  const ix = (screenX - viewportW / 2) / cam.zoom + cam.panX;
  const iy = (screenY - viewportH / 2) / cam.zoom + cam.panY;
  return isoToTile(ix, iy);
}

/**
 * Compute the inclusive tile range visible in the viewport under the given
 * camera transform, plus a `margin` tile buffer on each side. Used by the
 * renderer's culling pass (CLIENT_DESIGN §4.8).
 */
export function visibleTileRange(
  cam: CameraState,
  viewportW: number,
  viewportH: number,
  margin: number,
): { minX: number; maxX: number; minY: number; maxY: number } {
  // Corners of the screen in iso-space, reverse-projected to tile space.
  const corners = [
    screenToWorld(0, 0, cam, viewportW, viewportH),
    screenToWorld(viewportW, 0, cam, viewportW, viewportH),
    screenToWorld(0, viewportH, cam, viewportW, viewportH),
    screenToWorld(viewportW, viewportH, cam, viewportW, viewportH),
  ];
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const c of corners) {
    if (c.x < minX) minX = c.x;
    if (c.x > maxX) maxX = c.x;
    if (c.y < minY) minY = c.y;
    if (c.y > maxY) maxY = c.y;
  }
  return {
    minX: Math.floor(minX) - margin,
    maxX: Math.ceil(maxX) + margin,
    minY: Math.floor(minY) - margin,
    maxY: Math.ceil(maxY) + margin,
  };
}

/**
 * Depth-sort key for a renderable at tile `(x, y)` on layer `layer`.
 * Matches CLIENT_DESIGN §4.7:
 *
 *   key = tile_y * 10000 + tile_x * 100 + layer
 *
 * Layer values 0..7 within a tile — floor, floor overlay, props-on-floor,
 * characters, walls, roof, UI overlay. PixiJS `zIndex` + `sortableChildren`
 * picks up the numeric key; lower = further back.
 */
export function depthSortKey(
  tileX: number,
  tileY: number,
  layer: number,
): number {
  return tileY * 10_000 + tileX * 100 + layer;
}

/** Named layer constants used by `depthSortKey` and the scene graph. */
export const Z_FLOOR = 0;
export const Z_FLOOR_OVERLAY = 1;
export const Z_PROP_ON_FLOOR = 2;
export const Z_CHARACTER = 3;
export const Z_WALL = 4;
export const Z_ROOF = 5;
export const Z_UI_OVERLAY = 6;

/** Camera zoom bounds per `config.client.camera`. The 5 discrete levels the
 *  player wheels through; continuous zoom is 1.5+ polish. */
export const ZOOM_LEVELS: readonly number[] = [0.5, 0.75, 1.0, 1.5, 2.0];
export const DEFAULT_ZOOM_INDEX = 2; // 1.0 ×
export const INDOOR_ZOOM_INDEX = 1; // 0.75 × snap when entering a building

/** Clamp a zoom index to the legal range. */
export function clampZoomIndex(i: number): number {
  if (i < 0) return 0;
  if (i >= ZOOM_LEVELS.length) return ZOOM_LEVELS.length - 1;
  return i;
}
